import Tesseract from 'tesseract.js';
import { ScanCategory, ScanResult, ScannedItem } from '../types';
import { v4 as uuidv4 } from 'uuid';

const OCR_LANGUAGES = 'eng+fra';
const PRICE_PATTERN = /(-?\$?\s?[0-9OolI|]{1,3}(?:[.,][0-9OolI|]{3})*(?:[.,][0-9OolI|]{2}))/;
const QUANTITY_PATTERN = /^(\d+\s?[xX@]\s?|\d+\s)/;
const PRICE_ONLY_PATTERN = new RegExp(`^\\s*${PRICE_PATTERN.source}\\s*$`);
const TRAILING_PRICE_PATTERN = /(-?\$?\s?[0-9OolI|]{1,3}(?:[.,][0-9OolI|]{3})*(?:[.,][0-9OolI|]{2}))\s*$/;
const MAX_OCR_DIMENSION = 2200;
const OCR_JPEG_QUALITY = 0.92;

const loadImageElement = (file: File): Promise<HTMLImageElement> => new Promise((resolve, reject) => {
  const objectUrl = URL.createObjectURL(file);
  const image = new Image();
  image.onload = () => {
    URL.revokeObjectURL(objectUrl);
    resolve(image);
  };
  image.onerror = () => {
    URL.revokeObjectURL(objectUrl);
    reject(new Error('Unable to decode image'));
  };
  image.src = objectUrl;
});

const canvasToBlob = (canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(blob => {
      if (!blob) {
        reject(new Error('Failed to prepare image for OCR'));
        return;
      }
      resolve(blob);
    }, type, quality);
  });

const preprocessSingleVariantForOCR = async (imageElement: HTMLImageElement, rotation: 0 | 90 | 270): Promise<Blob> => {
  const sourceWidth = imageElement.naturalWidth || imageElement.width;
  const sourceHeight = imageElement.naturalHeight || imageElement.height;
  const scale = Math.min(1, MAX_OCR_DIMENSION / Math.max(sourceWidth, sourceHeight));
  const scaledWidth = Math.max(1, Math.round(sourceWidth * scale));
  const scaledHeight = Math.max(1, Math.round(sourceHeight * scale));

  const rotated = rotation !== 0;
  const canvas = document.createElement('canvas');
  canvas.width = rotated ? scaledHeight : scaledWidth;
  canvas.height = rotated ? scaledWidth : scaledHeight;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) {
    throw new Error('Unable to initialize canvas context for OCR preprocessing');
  }

  if (rotation === 90) {
    ctx.translate(canvas.width, 0);
    ctx.rotate(Math.PI / 2);
  } else if (rotation === 270) {
    ctx.translate(0, canvas.height);
    ctx.rotate(-Math.PI / 2);
  }

  ctx.drawImage(imageElement, 0, 0, scaledWidth, scaledHeight);

  // Apply lightweight grayscale + contrast boost for better OCR on receipt paper.
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const pixels = imageData.data;
  for (let i = 0; i < pixels.length; i += 4) {
    const gray = (pixels[i] * 0.299) + (pixels[i + 1] * 0.587) + (pixels[i + 2] * 0.114);
    const boosted = Math.max(0, Math.min(255, (gray - 128) * 1.25 + 128));
    pixels[i] = boosted;
    pixels[i + 1] = boosted;
    pixels[i + 2] = boosted;
  }
  ctx.putImageData(imageData, 0, 0);

  return canvasToBlob(canvas, 'image/jpeg', OCR_JPEG_QUALITY);
};

const preprocessImageForOCR = async (imageFile: File): Promise<Array<Blob | File>> => {
  if (typeof window === 'undefined') {
    return [imageFile];
  }

  const imageElement = await loadImageElement(imageFile);
  const sourceWidth = imageElement.naturalWidth || imageElement.width;
  const sourceHeight = imageElement.naturalHeight || imageElement.height;
  if (!sourceWidth || !sourceHeight) {
    return [imageFile];
  }

  const variants: Array<Blob | File> = [await preprocessSingleVariantForOCR(imageElement, 0)];
  const isLikelySideways = sourceWidth > sourceHeight * 1.15;
  if (isLikelySideways) {
    variants.push(await preprocessSingleVariantForOCR(imageElement, 90));
    variants.push(await preprocessSingleVariantForOCR(imageElement, 270));
  }

  return variants;
};

// --- 1. CLUSTERING LOGIC (Fixes column layout issues) ---
const clusterLines = (words: Tesseract.Word[]) => {
  if (!words.length) return [];

  // Sort words by Y position (top to bottom)
  const sorted = [...words].sort((a, b) => {
    const ay = (a.bbox.y0 + a.bbox.y1) / 2;
    const by = (b.bbox.y0 + b.bbox.y1) / 2;
    return ay - by;
  });

  const heights = sorted
    .map(word => Math.max(1, word.bbox.y1 - word.bbox.y0))
    .sort((a, b) => a - b);
  const medianHeight = heights[Math.floor(heights.length / 2)] || 12;
  const yThreshold = Math.max(10, Math.min(28, Math.round(medianHeight * 0.75)));

  const lineGroups: Tesseract.Word[][] = [];
  let currentLineWords: Tesseract.Word[] = [];
  let currentLineY = -1;

  for (const word of sorted) {
    const y = (word.bbox.y0 + word.bbox.y1) / 2;

    if (currentLineY === -1 || Math.abs(currentLineY - y) <= yThreshold) {
      currentLineWords.push(word);
      currentLineY = currentLineY === -1 ? y : ((currentLineY * (currentLineWords.length - 1)) + y) / currentLineWords.length;
      continue;
    }

    lineGroups.push(currentLineWords);
    currentLineWords = [word];
    currentLineY = y;
  }

  if (currentLineWords.length) {
    lineGroups.push(currentLineWords);
  }

  return lineGroups
    .map(group => group
      .sort((a, b) => a.bbox.x0 - b.bbox.x0)
      .map(word => word.text)
      .join(' ')
      .trim()
    )
    .filter(Boolean);
};

// --- 2. PARSING LOGIC (Extracts Name & Price) ---
const normalizePriceToken = (token: string): string =>
  token
    .replace(/[Oo]/g, '0')
    .replace(/[I|l]/g, '1')
    .replace(/\s+/g, '');

const parsePriceAmount = (rawPrice: string): number | null => {
  const cleaned = normalizePriceToken(rawPrice);
  const stripped = cleaned.replace(/[^0-9,.-]/g, '');
  if (!stripped) return null;

  const isNegative = stripped.includes('-');
  const unsigned = stripped.replace(/-/g, '');
  if (!unsigned) return null;

  const lastDot = unsigned.lastIndexOf('.');
  const lastComma = unsigned.lastIndexOf(',');

  const toNumber = (normalized: string) => {
    const parsed = Number.parseFloat(normalized);
    if (!Number.isFinite(parsed)) return null;
    return isNegative ? -parsed : parsed;
  };

  if (lastDot !== -1 && lastComma !== -1) {
    const decimalIndex = Math.max(lastDot, lastComma);
    const integerPart = unsigned.slice(0, decimalIndex).replace(/[.,]/g, '');
    const fractionalPart = unsigned.slice(decimalIndex + 1).replace(/[.,]/g, '');
    const normalized = fractionalPart.length > 0 ? `${integerPart}.${fractionalPart}` : integerPart;
    return toNumber(normalized);
  }

  const separator = lastDot !== -1 ? '.' : (lastComma !== -1 ? ',' : '');
  if (!separator) {
    return toNumber(unsigned);
  }

  const parts = unsigned.split(separator);
  if (parts.length === 1) {
    return toNumber(parts[0]);
  }

  if (parts.length === 2) {
    const [left, right] = parts;
    if (right.length === 2 || right.length === 1) {
      return toNumber(`${left}.${right}`);
    }
    return toNumber(`${left}${right}`);
  }

  const lastPart = parts[parts.length - 1];
  const leading = parts.slice(0, -1).join('');
  if (lastPart.length === 2) {
    return toNumber(`${leading}.${lastPart}`);
  }
  return toNumber(`${leading}${lastPart}`);
};

const parseLine = (line: string): { name: string, price: number } | null => {
  const trailingMatch = line.match(TRAILING_PRICE_PATTERN);
  const allMatches: RegExpMatchArray[] = [];
  const globalPriceRegex = new RegExp(PRICE_PATTERN.source, 'g');
  let scanMatch: RegExpExecArray | null = globalPriceRegex.exec(line);
  while (scanMatch) {
    allMatches.push(scanMatch);
    scanMatch = globalPriceRegex.exec(line);
  }
  const match = trailingMatch ?? (allMatches.length ? allMatches[allMatches.length - 1] : null);
  if (!match) return null;

  const matchedPrice = match[0];
  const parsedPrice = parsePriceAmount(matchedPrice);
  if (parsedPrice === null) return null;
  const isNegativeLine = line.includes('-') && !matchedPrice.includes('-');
  const price = isNegativeLine ? -Math.abs(parsedPrice) : parsedPrice;

  // Extract Name (everything before the selected price token)
  const matchIndex = typeof match.index === 'number' ? match.index : line.lastIndexOf(matchedPrice);
  let name = line.substring(0, Math.max(0, matchIndex)).trim();
  
  // Clean Name (remove quantity "1x" or "2 @")
  const quantityMatch = name.match(QUANTITY_PATTERN);
  if (quantityMatch) {
    name = name.substring(quantityMatch[0].length).trim();
  }
  name = name
    .replace(/[._]/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim();

  return { name, price };
};

const mergePriceOnlyLines = (lines: string[]) => {
  const merged: string[] = [];
  for (const line of lines) {
    const trimmed = line.trim();
    const previous = merged[merged.length - 1];
    const isPriceOnly = PRICE_ONLY_PATTERN.test(trimmed);

    if (isPriceOnly && previous && !PRICE_PATTERN.test(previous)) {
      merged[merged.length - 1] = `${previous} ${trimmed}`;
      continue;
    }

    merged.push(line);
  }
  return merged;
};

const normalizeOCRLine = (line: string) => {
  const normalized = line
    .replace(/[\u2018\u2019`]/g, "'")
    .replace(/[\u201c\u201d]/g, '"')
    .replace(/[\u00a0]/g, ' ')
    .replace(/([\p{L}])(\d)/gu, '$1 $2')
    .replace(/(\d)([\p{L}])/gu, '$1 $2')
    .replace(/\s+\.\s+/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .replace(/^[:;,-]+/, '')
    .replace(/[:;,.-]+$/, '')
    .replace(/\s+/g, ' ')
    .trim();

  return normalized;
};

// --- 3. CATEGORIZATION LOGIC ---
const detectCategory = (name: string): ScanCategory => {
  const upper = name.toUpperCase();
  if (upper.includes("TAX") || upper.includes("GST") || upper.includes("VAT")) return ScanCategory.Tax;
  if (upper.includes("TIP") || upper.includes("GRAT")) return ScanCategory.Gratuity;
  if (upper.includes("KITCHEN") || upper.includes("FEE")) return ScanCategory.Fee;
  return ScanCategory.Item;
};

// --- 4. MAIN EXPORTED FUNCTION ---

/**
 * Notes:
 * - `scanReceipt` creates and terminates its own Tesseract worker so each call
 *   is self-contained; callers should await the Promise to ensure the worker
 *   shuts down before continuing.
 * - The returned `items` are filtered to drop known totals / payment lines while
 *   `lines` represents the normalized text shown to users and `rawLines`
 *   captures the clustering output before token correction.
 * - Category detection is heuristic-based (tax, tip, fees are inferred from keywords),
 *   so unusual receipt lines might still be classified as `ScanCategory.Item`.
 */
export const scanReceipt = async (imageFile: File): Promise<ScanResult> => {
  const worker = await Tesseract.createWorker(OCR_LANGUAGES);
  let data: any;
  try {
    const preparedImages = await preprocessImageForOCR(imageFile);
    let bestData: any = null;
    let bestScore = Number.NEGATIVE_INFINITY;

    for (const preparedImage of preparedImages) {
      const result = await worker.recognize(preparedImage);
      const candidate = result.data as any;
      const confidence = typeof candidate.confidence === 'number' ? candidate.confidence : 0;
      const wordCount = Array.isArray(candidate.words) ? candidate.words.length : 0;
      const score = confidence + Math.min(120, wordCount) * 0.4;
      if (score > bestScore) {
        bestScore = score;
        bestData = candidate;
      }
    }

    data = bestData ?? {};
  } finally {
    await worker.terminate();
  }

  const wordLines = Array.isArray(data.words) && data.words.length > 0 
    ? clusterLines(data.words)
    : [];
  const textLines = !wordLines.length && typeof data.text === 'string'
    ? data.text
        .split(/\r?\n/)
        .map((line: string) => line.trim())
        .filter((line: string): line is string => Boolean(line))
    : [];
  const lines = wordLines.length ? wordLines : textLines;
  const normalizedLines = mergePriceOnlyLines(lines);
  const correctedLines = normalizedLines.map(normalizeOCRLine);
  const items: ScannedItem[] = [];

  // Parse from pre-clean lines to avoid over-correction hurting extraction quality.
  for (const line of normalizedLines) {
    // Skip known junk lines
    if (["TOTAL", "SUBTOTAL", "BALANCE", "VISA"].some(w => line.toUpperCase().includes(w))) continue;

    const parsed = parseLine(line);
    // Only add if we successfully parsed a price and the name is long enough
    if (parsed && parsed.name.length >= 2) {
      items.push({
        id: uuidv4(),
        name: parsed.name,
        price: parsed.price,
        category: detectCategory(parsed.name)
      });
    }
  }
  
  return { items, lines: correctedLines, rawLines: normalizedLines };
};
